import {
  Alert,
  Box,
  CircularProgress,
  Fade,
  Grow,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { getStoredToken } from "../utils/getStoredToken";
import { PartnerSafeApi } from './EnvFiles';
import HistoryDialog from './HistoryDialog';
import RecordsTable from './RecordsTable';
import RejectionReasonDialog from './RejectionReasonDialog';
import ReviewDialog from './ReviewDialog';
import SummaryCards from './SummaryCards';
import ZoomDialog from './ZoomDialog';
import { sortImages } from '../utils/sortImages';
import { RoleName } from '../models/RoleName';

const Dashboard = ({ roles = [] }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [chartData, setChartData] = useState({ pendentes: 0, aprovados: 0, reprovados: 0, total: 0 });
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [currentIdRegistro, setCurrentIdRegistro] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [openRejectionReasonDialog, setOpenRejectionReasonDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [imageStatuses, setImageStatuses] = useState({});
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [currentRejectId, setCurrentRejectId] = useState(null);
  const [currentRejectValue, setCurrentRejectValue] = useState('');
  const [openRejectionDialog, setOpenRejectionDialog] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [isRejectRegister, setIsRejectRegister] = useState(false);
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);
  const [toastContent, setToastContent] = useState(null);

  const fetchTableData = async (showLoading = false) => {
    const token = getStoredToken();

    if (showLoading) {
      setLoading(true);
    }

    setError(null);

    try {
      const response = await axios.get(
        `${PartnerSafeApi}/api/v1/Processos6Ros/ListarProcessos6Ros`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = response.data;
      const pendingRecords = data.filter(record => record.status === 'Pendente');

      setTableData(data);
      setChartData({
        pendentes: pendingRecords.length,
        aprovados: data.filter(record => record.status === 'Aprovado').length,
        reprovados: data.filter(record => record.status === 'Rejeitado').length,
        total: data.length
      });
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Entre em contato com o ponto focal do seu contrato, pois ainda não foi atribuído um centro de serviços ao seu usuário.');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const token = getStoredToken();

    if (token) {
      fetchTableData(true);
    }

    const interval = setInterval(() => {
      const token = getStoredToken();

      if (token) {
        fetchTableData(false);
      }
    }, 1000 * 10);

    return () => clearInterval(interval);
  }, []);

  const handleOpenDialog = (correlationId, idRegistro) => {
    setCurrentIdRegistro(idRegistro);
    setOpenDialog(true);
    fetchImages(correlationId, true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedImages([]);
    setCurrentIdRegistro(null);
  };

  const handleOpenHistoryDialog = (correlationId, idRegistro) => {
    setCurrentIdRegistro(idRegistro);
    setOpenHistoryDialog(true);
    fetchImages(correlationId, false);
  };

  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
    setSelectedImages([]);
    setCurrentIdRegistro(null);
  };

  const handleOpenZoom = (image, canReview = false) => {
    setIsReview(canReview)
    setZoomedImage(image);
  };

  const handleCloseZoom = () => {
    setZoomedImage(null);
  };

  const fetchImages = async (idRegistro, onlyPending = true) => {
    const token = getStoredToken();

    try {
      const response = await fetch(
        `${PartnerSafeApi}/api/v1/Processos6Ros/ListarFotosPorRegistro6RoStream/${idRegistro}?somentePendentes=${onlyPending}`,
        {
          method: 'GET',
          headers: {
            Accept: 'text/plain; x-api-version=1.0',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao buscar imagens.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      while (true) {
        const { value, done } = await reader.read();

        if (done) {
          break;
        }

        const chunkText = decoder.decode(value, { stream: true });
        let cleanText = chunkText.trim();

        try {
          const jsonData = JSON.parse(cleanText);

          if (!!jsonData) {
            setSelectedImages((previous) => [...previous, jsonData]);
          }
        } catch (parseError) {
          console.error('Erro ao realizar o parse da imagem:', parseError);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar imagens:', error);
    }
  };

  const handleCloseRejectionReasonDialog = () => {
    setIsRejectRegister(false);
    setRejectionReason('');
    setOpenRejectionReasonDialog(false);
  };

  const handleOpenRejectionReasonDialog = () => {
    setIsRejectRegister(true);
    setRejectionReason('');
    setOpenRejectionReasonDialog(true);
  };

  const handleReject = async () => {
    const token = getStoredToken();

    try {
      await axios.post(
        `${PartnerSafeApi}/api/v1/Processos6Ros/RejeitarRegistro6Ro`,
        {
          registro6RoIdentificador: currentIdRegistro,
          motivoDaReprovacao: rejectionReason,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      handleCloseRejectionReasonDialog();
      handleCloseDialog();
      fetchTableData();
    } catch (err) {
      console.error('Erro ao reprovar registro:', err);
    }
  };

  const handleRejectProcesso = async () => {
    const token = getStoredToken();

    setIsSubmittingReject(true);

    try {
      await axios.post(
        `${PartnerSafeApi}/api/v1/Processos6Ros/RejeitarProcesso6Ro`,
        {
          registro6RoIdentificador: currentIdRegistro,
          motivoDaReprovacao: rejectionReason,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      handleCloseRejectionReasonDialog();
      handleCloseDialog();
      fetchTableData();
      setToastContent({
        type: 'success',
        message: 'Registro rejeitado com sucesso!',
      })
    } catch (err) {
      setToastContent({
        type: 'error',
        message: 'Erro ao rejeirar registro. Por favor, tente novamente.',
      })
      console.error('Erro ao reprovar processo:', err);
    } finally {
      setIsSubmittingReject(false);
    }
  };

  const imagensOrdenadas = useMemo(() => (
    !!selectedImages ? sortImages(selectedImages) : []
  ), [selectedImages]);

  const nextZoomedImage = useMemo(() => {
    const zoomedIndex = imagensOrdenadas.findIndex(
      (img) => img.identificador === zoomedImage?.identificador,
    );

    if (zoomedIndex < imagensOrdenadas.length - 1) {
      return imagensOrdenadas[zoomedIndex + 1]
    }

    return null;
  }, [imagensOrdenadas, zoomedImage]);

  const previousZoomedImage = useMemo(() => {
    const zoomedIndex = imagensOrdenadas.findIndex(
      (img) => img.identificador === zoomedImage?.identificador,
    );

    if (zoomedIndex > 0) {
      return imagensOrdenadas[zoomedIndex - 1]
    }

    return null;
  }, [imagensOrdenadas, zoomedImage]);

  const handleImageAction = (imagemId, action) => {
    console.log('Acionando ação:', imagemId, action);
    // Se já está com o mesmo status, limpa o status e o motivo (se rejeitado)
    if (imageStatuses[imagemId] === action) {
      setImageStatuses(prev => {
        const copy = { ...prev };
        delete copy[imagemId];
        return copy;
      });
      setRejectionReasons(prev => {
        const copy = { ...prev };
        if (action === 'reject') delete copy[imagemId];
        return copy;
      });
      return;
    }
    if (action === 'reject') {
      setCurrentRejectId(imagemId);
      setCurrentRejectValue(rejectionReasons[imagemId] || '');
      setOpenRejectionDialog(true);
    } else {
      setImageStatuses(prev => ({
        ...prev,
        [imagemId]: action
      }));
      setRejectionReasons(prev => {
        const copy = { ...prev };
        delete copy[imagemId];
        return copy;
      });
    }
  };

    const handleRejectionReasonConfirm = () => {
    setImageStatuses(prev => ({
      ...prev,
      [currentRejectId]: 'reject',
    }));
    setRejectionReasons(prev => ({
      ...prev,
      [currentRejectId]: currentRejectValue,
    }));
    setOpenRejectionDialog(false);
    setCurrentRejectId(null);
    setCurrentRejectValue('');
  };

  const handleRejectionReasonCancel = () => {
    setOpenRejectionDialog(false);
    setCurrentRejectId(null);
    setCurrentRejectValue('');
  };
  

  const handleChangeCurrentRejectValue = (value) => {
    setCurrentRejectValue(value)
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress
          size={60}
          sx={{
            color: 'primary.main',
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: (theme) => theme.palette.background.default,
        p: 3,
      }}
    >
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              mb: 4,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'text.primary',
              textAlign: 'center',
              background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Painel de Controle
          </Typography>
          {error ? (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                background: (theme) => theme.palette.mode === 'light'
                  ? 'rgba(255, 0, 0, 0.1)'
                  : 'rgba(255, 0, 0, 0.2)',
                border: (theme) => `1px solid ${theme.palette.error.main}`,
                borderRadius: 2,
              }}
            >
              {error}
            </Alert>
          ) : (
            <>
              <Grow in timeout={800}>
                <Box>
                  <SummaryCards chartData={chartData} />
                </Box>
              </Grow>

              <Grow in timeout={1000}>
                <Paper
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    background: (theme) => theme.palette.background.paper,
                    boxShadow: (theme) => theme.palette.mode === 'light'
                      ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
                      : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <RecordsTable
                    tableData={tableData}
                    onOpenDialog={handleOpenDialog}
                    onOpenHistoryDialog={handleOpenHistoryDialog}
                    tabValue={tabValue}
                    setTabValue={setTabValue}
                  />
                </Paper>
              </Grow>
            </>
          )}

          <ReviewDialog
            open={openDialog}
            onClose={handleCloseDialog}
            isImagesLoading={!selectedImages.length}
            selectedImages={selectedImages}
            onOpenZoom={handleOpenZoom}
            currentRejectValue={currentRejectValue}
            openRejectionDialog={openRejectionDialog}
            imageStatuses={imageStatuses}
            rejectionReasons={rejectionReasons}
            handleImageAction={handleImageAction}
            handleRejectionReasonCancel={handleRejectionReasonCancel}
            handleChangeCurrentRejectValue={handleChangeCurrentRejectValue}
            handleRejectionReasonConfirm={handleRejectionReasonConfirm}
            handleUpdateTableData={fetchTableData}
            handleOpenRejectionReasonDialog={handleOpenRejectionReasonDialog}
            canReview={roles && roles.some((r) => r.nome === RoleName.ANALISTA_CTMO)}
          />

          <HistoryDialog
            open={openHistoryDialog}
            onClose={handleCloseHistoryDialog}
            isImagesLoading={!selectedImages.length}
            selectedImages={selectedImages}
            onOpenZoom={handleOpenZoom}
          />

          <RejectionReasonDialog
            open={openRejectionReasonDialog}
            reason={rejectionReason}
            // isRejectRegister={isRejectRegister}
            isSubmittingReject={isSubmittingReject}
            onChangeReason={setRejectionReason}
            onReject={isRejectRegister
              ? handleRejectProcesso
              : handleReject}
            onClose={handleCloseRejectionReasonDialog}
          />

          <ZoomDialog
            image={zoomedImage}
            next={imagensOrdenadas.findIndex((img) => img.identificador === zoomedImage?.identificador)}
            open={!!zoomedImage}
            imageStatuses={imageStatuses}
            isReview={isReview}
            onClose={handleCloseZoom}
            onClickPrevious={!!previousZoomedImage
              ? () => setZoomedImage(previousZoomedImage)
              : null
            }
            onClickNext={!!nextZoomedImage
              ? () => setZoomedImage(nextZoomedImage)
              : null
            }
            onApprove={(image) => handleImageAction(image.identificador, 'approve')}
            onReject={(image) => handleImageAction(image.identificador, 'reject')}
          />
        </Box>
      </Fade>
      {toastContent?.type && !!toastContent?.message?.length && (
        <Snackbar
          open={!!toastContent}
          autoHideDuration={4000} // 4 segundos
          onClose={() => setToastContent(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setToastContent(false)}
            severity={toastContent.type}
            sx={{ width: '100%' }}
          >
            {toastContent.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Dashboard;